import type { LockedPositionStore, StakingPositionStore, UnwindingPositionStore } from "@/hooks";
import { convertWeekStringToDurationNumber, getNextEpoch } from "@/utils";
import { type MouseEvent, useCallback, useMemo, useState } from "react";
import { LOCKED_POSITION_AMMs } from "@/config";
import { getNewUnwindingEpochs } from "@/services";
import { isLockedPosition } from "../../portfolio-positions.utils";
import { PositionStatus } from "../row/position-table-row.utils";

type Position = LockedPositionStore | StakingPositionStore | UnwindingPositionStore;

type UsePortfolioPositionsTableRowMenuArgs = {
    positionType: "locked" | "staked";
    alreadyVoted: boolean;
    unwindingEpochs: number;
    openDialog: (args: {
        dialogType: string;
        dialogProps: Record<string, unknown>;
    }) => void;
};

export const usePortfolioPositionsTableRowMenu = ({
    positionType,
    alreadyVoted,
    unwindingEpochs,
    openDialog,
}: UsePortfolioPositionsTableRowMenuArgs) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuPosition, setMenuPosition] = useState<Position | null>(null);
    const [menuIndex, setMenuIndex] = useState<number | null>(null);
    const [menuStatus, setMenuStatus] = useState<PositionStatus | undefined>(undefined);

    const isMenuOpen = useMemo(() => Boolean(menuAnchorEl), [menuAnchorEl]);

    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setMenuPosition(null);
        setMenuIndex(null);
        setMenuStatus(undefined);
    }, []);

    const handleMenuToggle = useCallback(
        (event: MouseEvent<HTMLElement>, position: Position, index: number, status?: PositionStatus) => {
            if (menuAnchorEl === event.currentTarget) {
                handleMenuClose();
                return;
            }
            setMenuAnchorEl(event.currentTarget);
            setMenuPosition(position);
            setMenuIndex(index);
            setMenuStatus(status);
        },
        [handleMenuClose, menuAnchorEl],
    );

    const isUnwindingPosition = useCallback(
        (position: Position): position is UnwindingPositionStore => "startUnwindingTimestampMs" in position,
        [],
    );

    const canStartUnlock = useCallback(
        (position: Position) => {
            if (!isLockedPosition(position)) {
                return false;
            }
            const parseLockDurationNumber = Number(position.lockDuration.split("w")[0]);
            return unwindingEpochs !== parseLockDurationNumber || !alreadyVoted;
        },
        [alreadyVoted, unwindingEpochs],
    );

    const handleWithdraw = useCallback(() => {
        if (!menuPosition) {
            return;
        }
        handleMenuClose();

        if (positionType === "locked" && menuStatus === PositionStatus.UNLOCKED && isUnwindingPosition(menuPosition)) {
            openDialog({
                dialogType: "WITHDRAW_UNWOUND_CONFIRM",
                dialogProps: {
                    amount: Number(menuPosition.lockedBalance),
                    unwindingTimestamp: menuPosition.startUnwindingTimestampMs,
                },
            });
            return;
        }

        if (positionType === "locked" && isLockedPosition(menuPosition)) {
            openDialog({
                dialogType: "WITHDRAW_LOCK_FORM",
                dialogProps: {
                    existingLockAmount: Number(menuPosition.lockedBalance),
                    existingDuration: convertWeekStringToDurationNumber(menuPosition.lockDuration),
                    position: menuPosition,
                },
            });
            return;
        }

        openDialog({
            dialogType: "WITHDRAW_FORM",
            dialogProps: {
                position: menuPosition,
            },
        });
    }, [handleMenuClose, isUnwindingPosition, menuPosition, menuStatus, openDialog, positionType]);

    const handleClaim = useCallback(() => {
        if (!menuPosition) {
            return;
        }
        handleMenuClose();
        openDialog({
            dialogType: "CLAIM_CONFIRM",
            dialogProps: {
                position: menuPosition,
            },
        });
    }, [handleMenuClose, menuPosition, openDialog]);

    const canCancelUnlock = useMemo(() => {
        if (!menuPosition || !isUnwindingPosition(menuPosition)) {
            return false;
        }
        return getNextEpoch() - 1 >= menuPosition.startUnwindingEpoch;
    }, [isUnwindingPosition, menuPosition]);

    const handleCancelUnlock = useCallback(() => {
        if (!menuPosition || !isUnwindingPosition(menuPosition)) {
            return;
        }
        handleMenuClose();

        const newUnwindingEpochs = getNewUnwindingEpochs(
            menuPosition.epochsRemaining,
            menuPosition.startUnwindingTimestampMs,
        );

        openDialog({
            dialogType: "CANCEL_EXIT_CONFIRM",
            dialogProps: {
                position: menuPosition,
                newUnwindingEpochs: Number(newUnwindingEpochs),
                unwindingTimestampMs: menuPosition.startUnwindingTimestampMs,
            },
        });
    }, [handleMenuClose, isUnwindingPosition, menuPosition, openDialog]);

    const handleExitEarly = useCallback((position: LockedPositionStore) => {
        const parseLockDurationNumber = Number(position.lockDuration.split("w")[0]);
        window.open(LOCKED_POSITION_AMMs[parseLockDurationNumber], "_blank");
    }, []);

    const canExitEarly = useCallback((position: Position) => {
        if (!isLockedPosition(position)) {
            return false;
        }
        const parseLockDurationNumber = Number(position.lockDuration.split("w")[0]);
        return Boolean(LOCKED_POSITION_AMMs[parseLockDurationNumber]);
    }, []);

    const handleChangeDuration = useCallback(
        (position: LockedPositionStore) => {
            handleMenuClose();
            openDialog({
                dialogType: "CHANGE_DURATION_FORM",
                dialogProps: {
                    existingLockAmount: Number(position.lockedBalance),
                    existingDuration: convertWeekStringToDurationNumber(position.lockDuration),
                    position,
                },
            });
        },
        [handleMenuClose, openDialog],
    );

    return {
        isMenuOpen,
        menuIndex,
        menuAnchorEl,
        menuPosition,
        menuStatus,
        handleMenuClose,
        handleMenuToggle,
        handleWithdraw,
        handleClaim,
        handleCancelUnlock,
        handleChangeDuration,
        canCancelUnlock,
        canStartUnlock,
        handleExitEarly,
        canExitEarly,
    };
};
